import { Card, CardHeader, CardTitle, CardFooter, CardContent, CardDescription } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useFunds } from "@/context/FundsContext";
import { useProfitTrends } from "@/context/ProfitTrendsContext";
import { formatCurrency } from '@/utils/formatCurrency';
import { formatDate } from "date-fns";
import { useEffect, useState } from "react";
import { ReactNode } from "react";
import { Button } from "@chakra-ui/react";

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatNumberRounded = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.floor(value));
};

const formatCurrencyRounded = (value: number) => {
  const floored = value >= 0 ? Math.floor(value) : -Math.floor(Math.abs(value));
  return formatCurrency(floored, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

type BaseMetricsProps = {
  title: string;
  value: string;
  fullValue?: string;
  shouldShowProgress?: boolean;
  progress?: number;
  shouldShowProgressTitle?: boolean;
  progressTitle?: ReactNode;
  secondaryProgressTitle?: ReactNode;
  progressTitleClassName?: string;
  secondaryProgressTitleClassName?: string;
};

type MetricsProps = {
  title: string;
  value: number;
  shouldShowProgress?: boolean;
  progress?: number;
  progressTitle?: ReactNode;
  secondaryProgressTitle?: ReactNode;
  progressTitleClassName?: string;
  secondaryProgressTitleClassName?: string;
};

export const BaseMetrics = ({value, fullValue, title, shouldShowProgress, progress, progressTitle, secondaryProgressTitle, progressTitleClassName, secondaryProgressTitleClassName}:BaseMetricsProps):React.ReactNode => {
  const getProgress = (progress: number): React.ReactNode => {
    return(
      <div>
        <CardContent>
            <div className="space-y-1">
            <div className={progressTitleClassName ?? "text-xs text-muted-foreground"}>
              {progressTitle}
            </div>
            {secondaryProgressTitle && (
              <div className={secondaryProgressTitleClassName ?? "text-xs text-muted-foreground"}>
                {secondaryProgressTitle}
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Progress value={progress} aria-label="%" />
        </CardFooter>
      </div>
    )
  }

  return (
    <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        {fullValue ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <CardTitle className="text-4xl cursor-default">{value}</CardTitle>
            </TooltipTrigger>
            <TooltipContent>{fullValue}</TooltipContent>
          </Tooltip>
        ) : (
          <CardTitle className="text-4xl">{value}</CardTitle>
        )}
      </CardHeader>
      <div className="mt-auto">
        {shouldShowProgress && getProgress(progress!)}
        {!shouldShowProgress && progressTitle &&
          <CardContent>
            <div className="space-y-1">
              <div className={progressTitleClassName ?? "text-xs text-muted-foreground"}>
                {progressTitle}
              </div>
              {secondaryProgressTitle && (
                <div className={secondaryProgressTitleClassName ?? "text-xs text-muted-foreground"}>
                  {secondaryProgressTitle}
                </div>
              )}
            </div>
          </CardContent>
        }
      </div>
    </Card>
  )
}

export const NumberMetrics = ({title, value, progress, progressTitle, secondaryProgressTitle, progressTitleClassName, secondaryProgressTitleClassName}:MetricsProps):React.ReactNode => {
  const valueString = value.toLocaleString();

  return (
    <BaseMetrics title={title} value={valueString} progress={progress} progressTitle={progressTitle} secondaryProgressTitle={secondaryProgressTitle} progressTitleClassName={progressTitleClassName} secondaryProgressTitleClassName={secondaryProgressTitleClassName}/>
  )
}


export const UsdMetrics = ({title, value, shouldShowProgress, progress, progressTitle, secondaryProgressTitle, progressTitleClassName, secondaryProgressTitleClassName}:MetricsProps):React.ReactNode => {
  const valueString = formatCurrencyRounded(value);
  const fullValueString = formatCurrency(value);

  return (
    <BaseMetrics title={title} value={valueString} fullValue={fullValueString} progress={progress} progressTitle={progressTitle} secondaryProgressTitle={secondaryProgressTitle} progressTitleClassName={progressTitleClassName} secondaryProgressTitleClassName={secondaryProgressTitleClassName} shouldShowProgress={shouldShowProgress}/>
  )
}

export const AllTimeMetrics = ({allTimeBalance, currentBalance}:{allTimeBalance: number | undefined, currentBalance: number | undefined}):React.ReactNode => {
  const allTimeValue = allTimeBalance ? allTimeBalance : 0;
  const allTimeValueString = formatCurrencyRounded(allTimeValue);
  const allTimeFullValueString = formatCurrency(allTimeValue);
  const currentValueString = formatCurrency(currentBalance ? currentBalance : 0);
  const progressTitle = `${currentValueString} in balance`;
  const progress = (currentBalance && allTimeBalance) ? (currentBalance / allTimeBalance) * 100: 0;

  return (
    <BaseMetrics title={"All time balance"} value={allTimeValueString} fullValue={allTimeFullValueString} progress={progress} progressTitle={progressTitle} shouldShowProgress/>
  )
}

export const PercentageMetrics = ({title, value, progress, progressTitle}:MetricsProps):React.ReactNode => {
  const valueString = formatNumber(value)+"%";

  return (
    <BaseMetrics title={title} value={valueString} progress={progress} progressTitle={progressTitle}/>
  )
}

export const MarginLevelMetrics = ({freeMargin, equity}:{freeMargin: number, balance:number, equity:number}):React.ReactNode => {
  const getMarginLevel = (equity: number, freeMargin:number) => {
    const usedMargin = equity - freeMargin;
    const marginLevel = (equity / usedMargin) * 100;

    return marginLevel;
  }

  const marginLevel = getMarginLevel(equity, freeMargin);
  const progressTitle = formatNumber(marginLevel) + '% margin level';
  const valueString = formatCurrencyRounded(freeMargin);
  const fullValueString = formatCurrency(freeMargin);

  const getMarginLevelProgress = (): React.ReactNode => {
    const logValue = Math.log10(marginLevel);
    const maxLogValue = 4; // Corresponds to 10,000%
    const progressValue = (Math.min(logValue, maxLogValue) / maxLogValue) * 100;

    let color = 'bg-green-500';
    //let progressLable = 'Safe';

    if (marginLevel <= 300) {
      color = 'bg-red-500';
      //progressLable = 'Risky';
    } else if (marginLevel <= 1000) {
      color = 'bg-orange-500';
      //progressLable = 'Pay attention';
    }

    return (
      <div>
        <CardContent>
          <div className="text-xs text-muted-foreground">
            {progressTitle}
          </div>
        </CardContent>
        <CardFooter>
          <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
            <div className={`${color} h-2.5 rounded-full`} style={{width: `${progressValue}%`}}></div>
          </div>
        </CardFooter>
      </div>
    )
  }

  return (
    <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardDescription>Free Margin</CardDescription>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <CardTitle className="text-4xl cursor-default">{valueString}</CardTitle>
          </TooltipTrigger>
          <TooltipContent>{fullValueString}</TooltipContent>
        </Tooltip>
      </CardHeader>
      <div className="mt-auto">
        {freeMargin > 0 && getMarginLevelProgress()}
      </div>
    </Card>
  )
}

type ActiveMoneyMetricsProps = {
  title: string;
  value: number;
  lastUpdatedAt: Date;
  onRefreshRequested?: () => void;
  activeMoneyPercentage: number;
};

export const ActiveMoneyMetrics = ({value, title, lastUpdatedAt, onRefreshRequested, activeMoneyPercentage}:ActiveMoneyMetricsProps):React.ReactNode => {
  const valueString = formatCurrencyRounded(value);
  const fullValueString = formatCurrency(value);
  const formattedDate = formatDate(lastUpdatedAt, "HH:mm MMM dd, yyyy");
  const [isOld, setIsOld] = useState(false);

  useEffect(() => {
    const checkIfOld = () => {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      setIsOld(new Date(lastUpdatedAt) < fiveMinutesAgo);
    };

    checkIfOld();
    const interval = setInterval(checkIfOld, 60000);

    return () => clearInterval(interval);
  }, [lastUpdatedAt]);

  const getActiveMoneyColor = (): { color: string; label: string } => {
    if (activeMoneyPercentage >= 25) {
      return { color: 'bg-red-600', label: 'Dangerous' };
    } else if (activeMoneyPercentage >= 15) {
      return { color: 'bg-red-400', label: 'Risky' };
    } else if (activeMoneyPercentage >= 8) {
      return { color: 'bg-orange-500', label: 'Warning' };
    }
    return { color: 'bg-green-500', label: 'Safe' };
  };

  const { color, label } = getActiveMoneyColor();
  const clampedPercentage = Math.min(activeMoneyPercentage, 100);

  return (
    <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <CardTitle className="text-4xl cursor-default">{valueString}</CardTitle>
          </TooltipTrigger>
          <TooltipContent>{fullValueString}</TooltipContent>
        </Tooltip>
      </CardHeader>
      <div className="mt-auto">
        {value ? (
          <div>
            <CardContent>
              <div className="text-xs text-muted-foreground">
                {activeMoneyPercentage.toFixed(2)}% of balance ({label})
              </div>
            </CardContent>
            <CardFooter>
              <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700" id={label}>
                <div className={`${color} h-2.5 rounded-full`} style={{width: `${clampedPercentage}%`}}></div>
              </div>
            </CardFooter>
          </div>
        ) : (
          <CardContent>
            <div className="text-xs text-muted-foreground">
              No active money
            </div>
          </CardContent>
        )}
        <CardFooter className="flex justify-between items-center pt-0">
          <p className={`text-xs ${isOld ? 'text-red-500' : 'text-muted-foreground'}`}>
            Last updated: {formattedDate} {isOld && <span className="text-red-500">!</span>}
          </p>
          <Button
            onClick={onRefreshRequested}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            disabled={!onRefreshRequested}
            aria-label="Refresh"
          >
            <RefreshIcon className="h-4 w-4" />
          </Button>
        </CardFooter>
      </div>
    </Card>
  )
}

const RefreshIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
  </svg>
);

type OvertimeProfitMetricsProps = {
  fundsLoaded: boolean;
  allTimeProfit: number | undefined;
  totalDeposits: number | undefined;
  totalWithdrawals: number | undefined;
};

export const OvertimeProfitMetrics = ({fundsLoaded, allTimeProfit, totalDeposits, totalWithdrawals}: OvertimeProfitMetricsProps): React.ReactNode => {
  const { profitTrends } = useProfitTrends();

  if (!fundsLoaded) {
    return (
      <BaseMetrics
        title="Overtime Profit"
        value="Loading..."
      />
    );
  }

  const profitPercentage = (allTimeProfit && totalDeposits && totalDeposits > 0) ? (allTimeProfit / totalDeposits) * 100 : 0;
  const valueString = formatNumber(profitPercentage) + '%';
  const withdrawnPercentage = (allTimeProfit && allTimeProfit > 0) ? Math.min(((totalWithdrawals ?? 0) / allTimeProfit) * 100, 100) : 0;
  const thisWeekProfit = profitTrends?.thisWeek?.profit ?? 0;
  const thisWeekChangePercentage = totalDeposits && totalDeposits > 0 ? (thisWeekProfit / totalDeposits) * 100 : 0;
  const weekSign = thisWeekChangePercentage > 0 ? '+' : thisWeekChangePercentage < 0 ? '-' : '±';
  const weeklyChangeClassName = thisWeekChangePercentage > 0
    ? "text-sm font-semibold text-green-600"
    : thisWeekChangePercentage < 0
      ? "text-sm font-semibold text-red-600"
      : "text-sm font-semibold text-muted-foreground";
  const weeklyChangeTitle = (
    <span className="flex justify-between items-center">
      <span>
        <span className={weeklyChangeClassName}>{weekSign}{formatNumber(Math.abs(thisWeekChangePercentage))}%</span>
        <span className="text-xs text-muted-foreground"> this week</span>
      </span>
      <span className="text-xs text-muted-foreground">{formatCurrencyRounded(thisWeekProfit)}</span>
    </span>
  );

  const thisYearProfit = profitTrends?.thisYear?.profit ?? 0;
  const thisYearChangePercentage = totalDeposits && totalDeposits > 0 ? (thisYearProfit / totalDeposits) * 100 : 0;
  const yearSign = thisYearChangePercentage > 0 ? '+' : thisYearChangePercentage < 0 ? '-' : '±';
  const yearChangeClassName = thisYearChangePercentage > 0
    ? "text-sm font-semibold text-green-600"
    : thisYearChangePercentage < 0
      ? "text-sm font-semibold text-red-600"
      : "text-sm font-semibold text-muted-foreground";
  const currentYear = new Date().getFullYear();
  const yearlyChangeTitle = (
    <span className="flex justify-between items-center">
      <span>
        <span className={yearChangeClassName}>{yearSign}{formatNumber(Math.abs(thisYearChangePercentage))}%</span>
        <span className="text-xs text-muted-foreground"> in {currentYear}</span>
      </span>
      <span className="text-xs text-muted-foreground">{formatCurrencyRounded(thisYearProfit)}</span>
    </span>
  );

  return (
    <BaseMetrics
      title="Overtime Profit"
      value={valueString}
      progressTitle={weeklyChangeTitle}
      secondaryProgressTitle={yearlyChangeTitle}
      shouldShowProgress
      progress={withdrawnPercentage}
    />
  );
};

type BalanceMetricsProps = {
  value: number;
  title: string;
}

export const BalanceMetrics = ({value, title}:BalanceMetricsProps):React.ReactNode => {
  const valueString = formatCurrencyRounded(value);
  const fullValueString = formatCurrency(value);

  const {loaded: fundsLoaded, transactions, fundsStats} = useFunds();

  let equitySubtitle;

  if (!fundsLoaded) {
    equitySubtitle='Loading funds details';
  }
  else if (!transactions || !fundsStats || transactions.length === 0) {
    equitySubtitle='Update your transactions to learn equity profit';
  }
  else {
    const allTimeBalance = fundsStats.allTimeBalance ? fundsStats.allTimeBalance : 0;
    equitySubtitle = 'All time balance: ' + formatCurrency(allTimeBalance);
  }

  return (
    <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <CardTitle className="text-4xl cursor-default">{valueString}</CardTitle>
          </TooltipTrigger>
          <TooltipContent>{fullValueString}</TooltipContent>
        </Tooltip>
      </CardHeader>
      <CardContent className="mt-auto">
        <p className="text-xs text-muted-foreground">
          {equitySubtitle}
        </p>
      </CardContent>
    </Card>
  )
}
